import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate, maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import { LoanForm } from './LoanForm';
import { SimulatorPanel } from './SimulatorPanel';
import { formatRate } from './types';
import type { DebtSummary, Installment, Loan, LoanDetail, PayoffQuote } from './types';
import { AdjustInstallmentDialog } from './AdjustInstallmentDialog';

type Tab = 'carteira' | 'simulador';

export function DebtsPage({ selectedIds }: { selectedIds: number[] }) {
  const [tab, setTab] = useState<Tab>('carteira');
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Loan | null>(null);
  const [paying, setPaying] = useState<{ loan: LoanDetail; installment: Installment } | null>(null);

  /** Dívida cuja prestação está sendo reajustada — RF-DIV-11. */
  const [adjusting, setAdjusting] = useState<LoanDetail | null>(null);
  const queryClient = useQueryClient();

  const debts = useQuery({
    queryKey: ['debts', selectedIds],
    queryFn: () =>
      api.get<{ data: Loan[]; meta: { summary: DebtSummary } }>('/debts', { entityIds: selectedIds }),
  });

  const reference = useQuery({
    queryKey: ['reference', selectedIds],
    queryFn: () => api.get<{ data: ReferenceData }>('/reference', { entityIds: selectedIds }),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Exclusão de dívida.
   *
   * Quem decide se pode é a API: dívida com parcela paga ou com liberação
   * registrada é recusada, porque os pagamentos ficariam apontando para uma
   * dívida que sumiu e o extrato teria dinheiro sem origem. Repetir essa regra
   * aqui criaria uma segunda versão dela, livre para divergir da primeira.
   */
  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/debts/${id}`, { entityIds: selectedIds }),
    onSuccess: (res) => {
      window.alert(res.message);
      refresh();
    },
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível excluir a dívida.'),
  });

  function refresh() {
    setCreating(false);
    setPaying(null);
    void queryClient.invalidateQueries({ queryKey: ['debts'] });
    void queryClient.invalidateQueries({ queryKey: ['debt'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const summary = debts.data?.meta.summary;
  const loans = debts.data?.data ?? [];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Dívidas</h1>
        {tab === 'carteira' && (
          <button
            onClick={() => setCreating(true)}
            disabled={!reference.data}
            className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            + Nova dívida
          </button>
        )}
      </div>

      <div className="mt-4 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {([['carteira', 'Minhas dívidas'], ['simulador', 'Simulador e comparador']] as const).map(
          ([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-3.5 py-2 text-sm transition ${
                tab === id
                  ? 'border-indigo-600 font-medium text-indigo-700 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              {label}
            </button>
          ),
        )}
      </div>

      <div className="mt-5">
        {tab === 'simulador' ? (
          <SimulatorPanel selectedIds={selectedIds} />
        ) : debts.isPending ? (
          <Skeleton />
        ) : debts.isError ? (
          <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
            Não foi possível carregar as dívidas.
          </p>
        ) : (
          <>
            {summary && loans.length > 0 && <SummaryBar summary={summary} />}

            {loans.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
                <p className="text-slate-500 dark:text-slate-400">
                  Nenhuma dívida cadastrada nas entidades selecionadas.
                </p>
                <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">
                  Cadastrar uma dívida aqui separa principal de juros em cada parcela — sem isso, a
                  parcela inteira vira despesa e o resultado do mês fica errado.
                </p>
                <button
                  onClick={() => setCreating(true)}
                  disabled={!reference.data}
                  className="mt-3 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  Cadastrar a primeira dívida
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {loans.map((loan) => (
                  <LoanCard
                    key={loan.id}
                    loan={loan}
                    isOpen={openId === loan.id}
                    onToggle={() => setOpenId(openId === loan.id ? null : loan.id)}
                    onEdit={() => setEditing(loan)}
                    onDelete={() => {
                      if (
                        window.confirm(
                          `Excluir "${loan.description}"?\n\n` +
                            'Somem a dívida e todas as parcelas. Se a entrada do dinheiro foi ' +
                            'registrada numa conta, ela sai junto — o saldo dessa conta volta ao ' +
                            'que era antes.\n\n' +
                            'Só não é possível quando alguma parcela já foi paga: ali houve ' +
                            'dinheiro saindo da conta, e isso se estorna pelo cronograma.',
                        )
                      ) {
                        remove.mutate(loan.id);
                      }
                    }}
                  />
                ))}
              </div>
            )}

            {openId !== null && (
              <LoanDetailPanel
                loanId={openId}
                selectedIds={selectedIds}
                onPay={(loan, installment) => setPaying({ loan, installment })}
                onAdjust={setAdjusting}
              />
            )}
          </>
        )}
      </div>

      {(creating || editing) && reference.data && (
        <LoanForm
          /* Remonta ao trocar de dívida: sem isto, abrir a segunda
             reaproveitaria os `useState` da primeira. */
          key={editing?.id ?? 'nova'}
          loan={editing}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); }}
        />
      )}

      {adjusting && (
        <AdjustInstallmentDialog
          loan={adjusting}
          selectedIds={selectedIds}
          onClose={() => setAdjusting(null)}
          onSaved={() => { setAdjusting(null); refresh(); }}
        />
      )}

      {paying && reference.data && (
        <PayInstallmentDialog
          loan={paying.loan}
          installment={paying.installment}
          reference={reference.data}
          selectedIds={selectedIds}
          onClose={() => setPaying(null)}
          onPaid={refresh}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------

function SummaryBar({ summary }: { summary: DebtSummary }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Stat label="Dívida total" value={formatMoney(summary.total_debt)} strong />
      <Stat
        label="Juros ainda por pagar"
        value={formatMoney(summary.interest_ahead)}
        hint="se nada mudar até o fim dos contratos"
        tone="warn"
      />
      <Stat label="Juros já pagos" value={formatMoney(summary.interest_paid_total)} />
      <Stat
        label="Maior CET em aberto"
        value={formatRate(summary.highest_rate_monthly)}
        hint="por onde começa a avalanche"
      />
    </div>
  );
}

function Stat({
  label, value, hint, strong, tone,
}: {
  label: string; value: string; hint?: string; strong?: boolean; tone?: 'warn';
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      <p
        className={`mt-0.5 ${strong ? 'text-lg font-semibold' : 'text-base font-medium'} ${
          tone === 'warn' ? 'text-amber-700 dark:text-amber-400' : ''
        }`}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function LoanCard({
  loan, isOpen, onToggle, onEdit, onDelete,
}: {
  loan: Loan;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const paidRatio =
    Number(loan.financed_amount) === 0
      ? 0
      : (Number(loan.total_principal_paid) / Number(loan.financed_amount)) * 100;

  const settled = loan.status === 'quitado';

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        isOpen
          ? 'border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/25'
          : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {loan.description}
            {settled && (
              <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                quitada
              </span>
            )}
            {loan.status === 'inadimplente' && (
              <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-700 dark:bg-rose-950 dark:text-rose-300">
                em atraso
              </span>
            )}
            {loan.status === 'em_carencia' && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700 dark:bg-amber-950 dark:text-amber-300">
                carência
              </span>
            )}
          </p>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
            {loan.creditor_name && `${loan.creditor_name} · `}
            {loan.entity_name} · {loan.installments_count}x {loan.amortization_system.toUpperCase()}
            {loan.next_due_date && ` · próx. ${formatDate(loan.next_due_date)}`}
          </p>
        </div>

        <div className="text-right">
          <p className="text-lg font-semibold">{formatMoney(loan.current_balance)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {/* O CET vem primeiro: é a taxa que compara. */}
            CET {formatRate(loan.cet_monthly)} a.m.
            <span className="text-slate-400"> · juros {formatRate(loan.interest_rate_monthly)}</span>
          </p>
        </div>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-emerald-500"
          style={{ width: `${Math.min(paidRatio, 100)}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">
        {formatMoney(loan.total_principal_paid)} de {formatMoney(loan.financed_amount)} amortizados
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button
          onClick={onToggle}
          className="text-[11px] text-indigo-600 hover:underline dark:text-indigo-400"
        >
          {isOpen ? '− fechar cronograma' : '+ ver cronograma e quitação'}
        </button>

        {/*
          Editar e excluir ficam SEMPRE visíveis, e a recusa vem da API com o
          motivo. Esconder o botão numa dívida já paga pareceria defeito da
          tela; mostrá-lo e explicar por que não dá ensina a regra.
        */}
        <button
          onClick={onEdit}
          className="ml-auto text-[11px] text-slate-500 hover:text-indigo-600 dark:text-slate-400"
        >
          Editar
        </button>
        <button
          onClick={onDelete}
          className="text-[11px] text-slate-500 hover:text-rose-600 dark:text-slate-400"
        >
          Excluir
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------

function LoanDetailPanel({
  loanId, selectedIds, onPay, onAdjust,
}: {
  loanId: number;
  selectedIds: number[];
  onPay: (loan: LoanDetail, installment: Installment) => void;
  /** Abre o reajuste da prestação — RF-DIV-11. */
  onAdjust: (loan: LoanDetail) => void;
}) {
  const [showPayoff, setShowPayoff] = useState(false);

  const detail = useQuery({
    queryKey: ['debt', loanId, selectedIds],
    queryFn: () => api.get<{ data: LoanDetail }>(`/debts/${loanId}`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (detail.isPending) {
    return <div className="mt-6 h-48 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />;
  }

  if (detail.isError) {
    return <p className="mt-6 text-sm text-rose-600">Erro ao carregar a dívida.</p>;
  }

  const loan = detail.data;
  const nextOpen = loan.installments.find((i) => i.status !== 'pago');
  const drift = Number(loan.balance_drift) !== 0;

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-3.5 dark:border-slate-800">
        <h2 className="font-medium">Cronograma · {loan.description}</h2>
        <div className="ml-auto flex gap-2">
          {/**
            * O reajuste fica JUNTO do cronograma, e não no cadastro.
            *
            * É o cronograma que ele muda — quem está olhando as parcelas é quem
            * acabou de receber a carta do banco dizendo que a prestação mudou.
            * No formulário de cadastro, ao lado de prazo e taxa contratados,
            * pareceria que se está corrigindo o contrato original.
            */}
          {nextOpen && (
            <button
              onClick={() => onAdjust(loan)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
            >
              Ajustar parcela
            </button>
          )}
          <button
            onClick={() => setShowPayoff((v) => !v)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
          >
            {showPayoff ? 'Fechar quitação' : 'Simular quitação'}
          </button>
          {nextOpen && (
            <button
              onClick={() => onPay(loan, nextOpen)}
              className="rounded-lg bg-emerald-600 px-3.5 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700"
            >
              Pagar parcela {nextOpen.number}
            </button>
          )}
        </div>
      </div>

      {/* O saldo gravado é cache; divergência aparece em vez de ficar escondida. */}
      {drift && (
        <p className="border-b border-amber-200 bg-amber-50 px-3.5 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          O saldo gravado difere do derivado do cronograma em {formatMoney(loan.balance_drift)}.
          Rode <code>sf invariants</code>.
        </p>
      )}

      {showPayoff && <PayoffBlock loanId={loanId} selectedIds={selectedIds} />}

      <div className="border-b border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm dark:border-slate-800 dark:bg-slate-800/40">
        <p className="text-slate-600 dark:text-slate-300">
          Cada parcela sai inteira da conta, mas só a coluna <strong>juros</strong> é despesa. O{' '}
          <strong>principal</strong> reduz a dívida e não entra no resultado do mês.
        </p>
      </div>

      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-slate-200 bg-white text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">#</th>
              <th className="px-3 py-2 font-medium">Vencimento</th>
              <th className="px-3 py-2 text-right font-medium">Parcela</th>
              <th className="px-3 py-2 text-right font-medium">Principal</th>
              <th className="px-3 py-2 text-right font-medium">Juros</th>
              <th className="px-3 py-2 text-right font-medium">Saldo depois</th>
              <th className="px-3 py-2 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loan.installments.map((i) => (
              <tr
                key={i.id}
                className={i.status === 'pago' ? 'text-slate-400 dark:text-slate-500' : ''}
              >
                <td className="px-3 py-1.5">{i.number}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{formatDate(i.due_date)}</td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium">
                  {formatMoney(i.total)}
                </td>
                {/* Duas colunas separadas, não um total que esconde a divisão. */}
                <td className="whitespace-nowrap px-3 py-1.5 text-right">
                  {formatMoney(i.principal)}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-amber-700 dark:text-amber-400">
                  {formatMoney(i.interest)}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-slate-500 dark:text-slate-400">
                  {formatMoney(i.balance_after)}
                </td>
                <td className="px-3 py-1.5">
                  {i.is_grace_period && (
                    <span className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                      carência
                    </span>
                  )}
                  {i.status === 'pago' ? (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      pago {i.paid_at && formatDate(i.paid_at)}
                    </span>
                  ) : i.is_overdue ? (
                    <span className="text-rose-600 dark:text-rose-400">vencida</span>
                  ) : (
                    <span className="text-slate-500 dark:text-slate-400">pendente</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoffBlock({ loanId, selectedIds }: { loanId: number; selectedIds: number[] }) {
  const quote = useQuery({
    queryKey: ['debt-payoff', loanId, selectedIds],
    queryFn: () =>
      api.get<{ data: PayoffQuote }>(`/debts/${loanId}/payoff`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (quote.isPending) {
    return <div className="h-24 animate-pulse bg-slate-100 dark:bg-slate-800" />;
  }

  if (quote.isError) {
    return <p className="p-3.5 text-sm text-rose-600">Erro ao calcular a quitação.</p>;
  }

  const q = quote.data;

  return (
    <div className="border-b border-slate-200 bg-indigo-50/50 p-3.5 dark:border-slate-800 dark:bg-indigo-950/20">
      <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
        Quitação antecipada de {q.installments_remaining} parcela(s)
      </p>

      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Soma nominal das parcelas</p>
          <p className="text-base line-through decoration-slate-400">{formatMoney(q.nominal_sum)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Valor de quitação</p>
          <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
            {formatMoney(q.payoff_amount)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Juros dispensados</p>
          <p className="text-base font-medium">{formatMoney(q.interest_waived)}</p>
        </div>
      </div>

      <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">{q.note}</p>
    </div>
  );
}

// -------------------------------------------------------------------

export function PayInstallmentDialog({
  loan, installment, reference, selectedIds, onClose, onPaid,
}: {
  loan: LoanDetail;
  installment: Installment;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onPaid: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const accounts = reference.accounts.filter((a) => a.entity_id === loan.entity_id);

  const [accountId, setAccountId] = useState(String(loan.account_id ?? accounts[0]?.id ?? ''));
  const [paidAt, setPaidAt] = useState(today);
  const [lateInterest, setLateInterest] = useState('');
  const [lateFee, setLateFee] = useState('');

  const extra = Number(parseInputToApi(lateInterest || '0')) + Number(parseInputToApi(lateFee || '0'));
  const expense = Number(installment.expense_share) + extra;
  const cashOut = Number(installment.principal) + expense;

  const pay = useMutation({
    mutationFn: () =>
      api.post(`/debts/${loan.id}/installments/${installment.id}/pay`, {
        account_id: Number(accountId),
        paid_at: paidAt,
        ...(lateInterest ? { late_interest: parseInputToApi(lateInterest) } : {}),
        ...(lateFee ? { late_fee: parseInputToApi(lateFee) } : {}),
      }, { entityIds: selectedIds }),
    onSuccess: onPaid,
  });

  const error = pay.error instanceof ApiError ? pay.error : null;

  return (
    <Modal title={`Pagar parcela ${installment.number}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); pay.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
          </div>
        )}

        {/* A explicação é o ponto da tela, não um detalhe (RN-61). */}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="font-medium text-indigo-900 dark:text-indigo-200">
            Só parte desta parcela é despesa.
          </p>
          <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
            O principal reduz a dívida — ele troca dinheiro por patrimônio, não é gasto. Só os juros
            e encargos entram no resultado do mês.
          </p>
        </div>

        <dl className="rounded-lg bg-slate-50 px-3.5 py-3 text-sm dark:bg-slate-800/50">
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Sai da conta</dt>
            <dd className="font-semibold">{formatMoney(cashOut.toFixed(2))}</dd>
          </div>
          <div className="mt-1 flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">↳ amortiza a dívida</dt>
            <dd>{formatMoney(installment.principal)}</dd>
          </div>
          <div className="mt-0.5 flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">↳ vira despesa</dt>
            <dd className="text-amber-700 dark:text-amber-400">{formatMoney(expense.toFixed(2))}</dd>
          </div>
          <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            <dt>Saldo devedor depois</dt>
            <dd>{formatMoney(installment.balance_after)}</dd>
          </div>
        </dl>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Conta" required>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              className="input w-full"
            >
              <option value="">Selecione</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Data do pagamento" required>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
        </div>

        {installment.is_overdue && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Juros de mora" hint="entram como despesa">
              <input
                value={lateInterest}
                onChange={(e) => setLateInterest(maskMoneyInput(e.target.value))}
                inputMode="numeric"
                placeholder="0,00"
                className="input w-full text-right"
              />
            </Field>
            <Field label="Multa">
              <input
                value={lateFee}
                onChange={(e) => setLateFee(maskMoneyInput(e.target.value))}
                inputMode="numeric"
                placeholder="0,00"
                className="input w-full text-right"
              />
            </Field>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={pay.isPending || accountId === ''}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
          >
            {pay.isPending ? 'Pagando…' : 'Confirmar pagamento'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-200 dark:bg-slate-800" />
        ))}
      </div>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="h-28 rounded-xl bg-slate-200 dark:bg-slate-800" />
      ))}
    </div>
  );
}
